/**
 * List - компонент списку для відображення колекцій даних
 *
 * Підтримує:
 * - Завантаження даних через REST API
 * - Пагінацію, сортування, фільтрацію
 * - Інтеграцію з ListContext
 * - Події життєвого циклу
 * - Різні режими відображення (через children)
 * - UIManager для уніфікованого управління UI
 */

import type { IListConfig } from '@/Core/Contracts/UI/List'
import { configManager } from '@/Core/Managers/Config'
import { MergeConfigAndPropsTask } from '@/Core/Tasks/Utils/MergeConfigAndPropsTask'
import * as React from 'react'
import { createListContext, ListContextProvider } from './ListContextProvider'
import type { ListStore } from './Stores/ListStore'

/**
 * Компонент List
 * Поєднує UIManager, ListStore та кастомну логіку завантаження даних
 */
export const List: React.FC<IListConfig & { listContext?: ListStore | null }> = ({ listContext, ...config }) => {
    const listConfig = configManager.get('ListConfig')

    // Об'єднання глобальної конфігурації з переданими props
    let mergedConfig =
        MergeConfigAndPropsTask({
            listConfig,
            props: config,
        }) || config

    mergedConfig.isObservable = true

    // Створення або оновлення контексту
    if (!listContext) {
        listContext = createListContext(mergedConfig)
    } else {
        listContext.mergeToInstance(mergedConfig)
    }

    // Гарантуємо що listContext завжди observable коли рендериться через List
    if (listContext) {
        listContext.isObservable = true
    }

    // Інтеграція з профайлером через методи UIManager
    React.useEffect(() => {
        // Логування ініціалізації компонента
        listContext?.addProfilerEvent('List:init', {
            hasConfig: !!mergedConfig,
            configKeys: Object.keys(mergedConfig || {}),
        })

        // Встановлення початкового статусу
        listContext?.setProfilerStatus('active')

        return () => {
            // Логування при знищенні компонента
            listContext?.addProfilerEvent('List:unmount')
            listContext?.setProfilerStatus('inactive')
        }
    }, [])

    // Логування змін конфігурації
    React.useEffect(() => {
        listContext?.addProfilerEvent('List:config-changed', {
            mergedConfig,
            listContext,
        })
    }, [mergedConfig, listContext])

    React.useEffect(() => {
        listContext?.actions.load()
    }, [])

    if (!listContext) {
        return null
    }

    return <ListContextProvider value={listContext}>{listContext.render()}</ListContextProvider>
}
